import React, { FC, useEffect, useMemo, useRef, useState } from 'react'
import "../css/jobSubmitted.css"
import "../css/jobSubmittedSl.css"
import "../css/jobSubmittedSt.css"
import "../css/jobSubmittedSm.css"
import SectionTitle from '../../../../component-module/component-services/title/section/ts/sectionTitle'
import RoundButton from '../../../../component-module/component-services/button/round-button/ts/roundButton'
import TextField from '../../../../component-module/component-services/input/text-field/ts/textField'
import TextArea from '../../../../component-module/component-services/input/textarea/ts/textArea'
import Checkbox from '../../../../component-module/component-services/checkbox/ts/checkbox'
import { CountryOption, CountrySelect } from '../../../../component-module/component-services/input/phone-number'
import { ValidationRuleCar } from './validationJobSumitRule'
import { sanitizeInput } from '../../../../context-module/context-configuration/permission-provider/validation'
import { useNavigate } from 'react-router-dom'
import { useToast } from '../../../../context-module/context-configuration/toast-provider/bottomToast/ts/ToastProvider'
import { applyJobApi, getCountry, mapCountryOptions } from './jobDetail.type'
import { useCmsPage } from '../../../../context-module/context-configuration/auth-provider/appShell/appShellContent/appUseCntData'
import { API_ENDPOINTS } from '../../../../context-module/context-configuration/auth-provider/apiGroup/apiProvider'
import { JobDetailPageData } from '../../career/ts/career.type'
import { useLoadingAni } from '../../../../context-module/context-configuration/auth-provider/loadinAni/LoadingAniProvider'

export interface SubmitJobSection {
    key: string;
    visible: boolean;
    content: {
        headerSection: {
            key: string;
            visible: boolean;
            label: string;
            title: string;
            description: string;
            subDescription: string;
            button: {
                label: string;
            };
        };
    }[];
}

export interface SubmitJobProps {
    dataJob?: SubmitJobSection; //NOSONAR
    jobUuid?: string;
    positionName?: string;
}

const JobSubmitForm: FC<SubmitJobProps> = ({ jobUuid, positionName }) => {
    const { data: cms } = useCmsPage<JobDetailPageData>(API_ENDPOINTS.ROUTE_URL.JOB_DETAIL);
    const submitJobSection: SubmitJobSection | undefined = useMemo(() => {
        return cms?.jobDetail?.cta?.find((x) => !!x.submitJobSection)?.submitJobSection;
    }, [cms]);

    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [formErrors, setFormErrors] = useState<Record<string, string>>({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const navigate = useNavigate();
    const { showToast } = useToast();
    const { playLoading } = useLoadingAni();
    const [countryOptions, setCountryOptions] = useState<CountryOption[]>([]);

    const expandData = submitJobSection?.content?.[0];

    const [formData, setFormData] = useState({
        fullName: '',
        email: '',
        country: '',
        phone: '',
        telephoneCode: '',
        jobPosition: positionName ?? /* istanbul ignore next */ '',
        message: '',
        consent: false
    });

    useEffect(() => {
        let alive = true;

        const fetchCountries = async () => {
            try {
                const res = await getCountry();
                /* istanbul ignore next */
                if (!alive) return;
                /* istanbul ignore next */
                const countries = res?.data?.data ?? [];
                setCountryOptions(mapCountryOptions(countries));
            } catch {
                /* istanbul ignore next */
                if (!alive) return;

                /* istanbul ignore next */
                setCountryOptions([]);
                /* istanbul ignore next */
                showToast({
                    status: "error",
                    title: "Country Load Failed!",
                    description: "Unable to load country list. Please try again later.",
                });
            }
        };

        fetchCountries();

        return () => {
            alive = false;
        };
    }, [showToast]);

    useEffect(() => {
        setFormData(prev => ({
            ...prev,
            jobPosition: positionName ?? /* istanbul ignore next */  prev.jobPosition,
        }));
    }, [positionName]);

    const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

    const handleSubmit = async (e?: React.FormEvent) => {
        e?.preventDefault();

        /* istanbul ignore next */
        if (isSubmitting) return;

        if (!jobUuid) {
            showToast({
                status: "error",
                title: "Missing Job ID",
                description: "Job ID not found. Please open the job detail again.",
            });
            return;
        }

        const validation = validateForm();

        if (!validation.isValid) {
            /* istanbul ignore next */
            if (validation.hasRequiredErrors) {
                showToast({
                    status: "warning",
                    title: "Incomplete Form!",
                    description: "Please complete all required fields before submitting.",
                });
            }

            if (validation.hasFormatErrors) {
                showToast({
                    status: "error",
                    title: "Invalid Format!",
                    description: "Please check and correct the highlighted fields.",
                });
            }

            return;
        }

        if (!selectedFile) {
            showToast({
                status: "warning",
                title: "File Required!",
                description: "Please attach your CV/Resume before submitting.",
            });
            return;
        }

        setIsSubmitting(true);
        playLoading();

        try {
            await wait(1000);

            await applyJobApi(jobUuid, {
                fullName: formData.fullName,
                email: formData.email,
                country: formData.country,
                telephoneCode: formData.telephoneCode,
                phone: formData.phone,
                position: formData.jobPosition,
                message: formData.message,
                consent: formData.consent,
                file: selectedFile,
            });

            showToast({
                status: "success",
                title: "Application Submitted Successfully!",
                description: "Your application has been received. We will review it and get back to you soon.",
            });

            setTimeout(() => {
                setFormData({
                    fullName: "",
                    email: "",
                    country: "",
                    phone: "",
                    telephoneCode: "",
                    jobPosition: positionName ?? /* istanbul ignore next */  "",
                    message: "",
                    consent: false,
                });

                setFormErrors({});
                setSelectedFile(null);

                /* istanbul ignore next */
                if (fileInputRef.current) {
                    fileInputRef.current.value = "";
                }

                navigate("/career");
            }, 700);
        } catch (error) {
            /* istanbul ignore next */
            if (error) {
                showToast({
                    status: "error",
                    title: "Submission Failed!",
                    description: "Your application was not submitted. Please try again!",
                });
            }

        } finally {
            setIsSubmitting(false);
        }
    };

    const validateForm = (): { isValid: boolean; hasRequiredErrors: boolean; hasFormatErrors: boolean } => { //NOSONAR
        const errors: Record<string, string> = {};
        let hasRequiredErrors = false;
        let hasFormatErrors = false;

        // Full name validation
        if (!formData.fullName) { //NOSONAR
            errors.fullName = 'required';
            hasRequiredErrors = true;
        } else {
            const nameError = ValidationRuleCar('fullName', formData.fullName);
            /* istanbul ignore next */
            if (nameError) {
                /* istanbul ignore next */
                errors.fullName = nameError;
                /* istanbul ignore next */
                hasFormatErrors = true;
            }
        }

        // Email validation
        if (!formData.email) { //NOSONAR
            errors.email = 'required';
            hasRequiredErrors = true;
        } else {
            const emailError = ValidationRuleCar('email', formData.email);
            if (emailError) {
                errors.email = emailError;
                hasFormatErrors = true;
            }
        }

        // Country validation
        if (!formData.country) {
            errors.country = 'required';
            hasRequiredErrors = true;
        }

        // Phone validation
        if (!formData.phone) { //NOSONAR
            errors.phone = 'required';
            hasRequiredErrors = true;
        } else {
            const phoneError = ValidationRuleCar('phone', formData.phone);
            /* istanbul ignore next */
            if (phoneError) {
                /* istanbul ignore next */
                errors.phone = phoneError;
                /* istanbul ignore next */
                hasFormatErrors = true;
            }
        }

        // Message validation (optional field)
        /* istanbul ignore next */
        if (formData.message?.trim()) {
            /* istanbul ignore next */
            const messageError = ValidationRuleCar('message', formData.message);
            /* istanbul ignore next */
            if (messageError) {
                /* istanbul ignore next */
                errors.message = messageError;
                /* istanbul ignore next */
                hasFormatErrors = true;
            }
        }

        // Consent validation
        /* istanbul ignore next */
        if (!formData.consent) {
            /* istanbul ignore next */
            errors.consent = 'required';
            /* istanbul ignore next */
            hasRequiredErrors = true;
        }

        setFormErrors(errors);
        return {
            isValid: Object.keys(errors).length === 0,
            hasRequiredErrors,
            hasFormatErrors
        };
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        /* istanbul ignore next */
        if (!file) return;

        const allowedTypes = [
            "application/pdf",
            "application/msword",
            "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        ];
        const maxSize = 5 * 1024 * 1024;

        if (!allowedTypes.includes(file.type)) {
            showToast({
                status: "error",
                title: "Invalid File Type!",
                description: "Please upload a PDF file.",
            });
            setSelectedFile(null);
            /* istanbul ignore next */
            if (fileInputRef.current) fileInputRef.current.value = "";
            return;
        }

        if (file.size > maxSize) {
            showToast({
                status: "error",
                title: "File Too Large!",
                description: "File size must be less than 5MB.",
            });
            setSelectedFile(null);
            /* istanbul ignore next */
            if (fileInputRef.current) fileInputRef.current.value = "";
            return;
        }

        setSelectedFile(file);
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        // Only validate format if field has value
        /* istanbul ignore next */
        if (value) {
            /* istanbul ignore next */
            const error = ValidationRuleCar(name, value);
            if (error) {
                setFormErrors(prev => ({ ...prev, [name]: error }));
            } else  /* istanbul ignore next */ if (formErrors[name] && formErrors[name] !== 'required') {
                // Clear error if value is now valid
                /* istanbul ignore next */
                setFormErrors(prev => ({ ...prev, [name]: '' }));
            }
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value, type } = e.target;
        const sanitizedValue = sanitizeInput(value);

        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? /* istanbul ignore next */ (e.target as HTMLInputElement).checked : sanitizedValue
        }));

        // Clear error when user starts typing
        if (formErrors[name]) {
            setFormErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const handleAttachClick = () => {
        fileInputRef.current?.click();
    };

    const onClickExpMore = () => {
        navigate("/career/position/all")
    }

    return (
        <section className='scl--jcbs-sub-app-bo-con-st'>
            {submitJobSection?.visible &&
                <div className="scl--jsabcs-bo-st">
                    <SectionTitle
                        label={expandData?.headerSection?.label}
                        title={expandData?.headerSection?.title}
                        className="scl--jbs-sub-jb-tex-st"
                    />

                    <div className="scl--jbs-car-ti-bo-dis-st">
                        <p className='p-jbs'>{expandData?.headerSection?.description}</p>
                        <span className='span-jbs'>{expandData?.headerSection?.subDescription}</span>

                        <RoundButton
                            text={expandData?.headerSection?.button?.label ?? /* istanbul ignore next */ "View all position"}
                            onClick={onClickExpMore}
                            className="scl--jctbds-btn-st"
                            showIcon={false}
                        />
                    </div>
                </div>
            }

            <div className='scl--jsbcs-bo-inp-co-st'>
                <form onSubmit={handleSubmit}>
                    <TextField
                        type="text"
                        name="fullName"
                        placeholder="Full name *"
                        value={formData.fullName}
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                        required
                        error={!!formErrors.fullName && formErrors.fullName !== 'required'}
                        warning={formErrors.fullName === 'required'}
                    />

                    <TextField
                        type="email"
                        name="email"
                        placeholder="E-mail *"
                        value={formData.email}
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                        required
                        error={!!formErrors.email && formErrors.email !== 'required'}
                        warning={formErrors.email === 'required'}
                    />

                    <div className="scl--jbcs-num-bo-st">
                        <div className="scl--jnbs-coun-se-bo-st">
                            <CountrySelect
                                name="country"
                                placeholder="Country *"
                                options={countryOptions}
                                value={formData.country}
                                onChange={(country: CountryOption) => {
                                    setFormData(prev => ({
                                        ...prev,
                                        country: country.code,
                                        telephoneCode: country.dialCode,
                                    }));

                                    /* istanbul ignore next */
                                    if (formErrors.country) {
                                        setFormErrors(prev => ({ ...prev, country: '' }));
                                    }
                                }}
                                required
                                error={!!formErrors.country && formErrors.country !== 'required'}
                                warning={formErrors.country === 'required'}
                            />
                        </div>

                        <div className="scl--jnbs-pho-num-bo-st">
                            <TextField
                                type="tel"
                                name="phone"
                                placeholder="Phone *"
                                value={formData.phone}
                                onChange={handleInputChange}
                                onBlur={handleBlur}
                                maxLength={20}
                                required
                                error={!!formErrors.phone && formErrors.phone !== 'required'}
                                warning={formErrors.phone === 'required'}
                            />
                        </div>
                    </div>

                    <TextField
                        type="text"
                        name="jobPosition"
                        placeholder="Position *"
                        value={formData.jobPosition}
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                        required
                        disabled
                    />

                    <TextArea
                        name="message"
                        placeholder="Message"
                        value={formData.message}
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                        maxLength={300}
                        rows={6}
                        error={!!formErrors.message}
                        errorText={formErrors.message}
                    />

                    <div className="scl--jbics-val-bo-ch-st">
                        <Checkbox
                            name="consent"
                            id="consent"
                            checked={formData.consent}
                            onChange={(checked) => setFormData(prev => ({ ...prev, consent: checked }))}
                        />
                        <span className="scl--jvbcs-des-bo-st">
                            By clicking the button Agree & send I give my consent to my personal data in accordance with <span className='scl--web-page-business-contact-consent-label-bold'> Privacy Statement </span>for the purpose of potential employment, internship and future career opportunities.
                        </span>
                    </div>

                    <div className="scl--fi-bo-up-co-st">
                        <div className="scl--fbucs-grp-at-up-st">
                            <RoundButton
                                text="Attach file"
                                onClick={handleAttachClick}
                                showIcon={false}
                                className="scl--fgaus-ro-bt-st"
                                type="button"
                            />
                            {selectedFile ?
                                <span className="scl--web-page-business-contact-file-name">
                                    {selectedFile.name}
                                </span>
                                :
                                <span className="scl--fgaus-sp-te-st">
                                    Upload CV / Resume<span className="scl--fsts-req-st">*</span> : PDF file less than 5MB .
                                </span>
                            }
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept=".pdf,.doc,.docx"
                                onChange={handleFileChange}
                                style={{ display: 'none' }}
                                autoComplete='off'
                            />
                        </div>

                        <RoundButton
                            text={isSubmitting ? "Submitting..." : "Submit"}
                            onClick={handleSubmit}
                            showIcon={false}
                            className="scl--fbucs-su-bt-cd-st"
                            disabled={!formData.consent || isSubmitting}
                            type='submit'
                        />
                    </div>
                </form>
            </div>
        </section>
    )
}

export default JobSubmitForm